import { useState, useEffect, useRef } from 'react'
import useSWRMutation from 'swr/mutation'
import { z } from 'zod'
import { reactMount } from '@/lib/reactMount'
import { ArrowRight } from '@/components/Icons'
import { FormField } from '@/components/FormField'
import { FormError } from '@/components/FormError'
import { Button } from '@/components/Button'
import { BrandLogo, BrandLogoIcon } from '@/components/Logos'
import { fetcher } from '@/lib/fetcher'

const stockTransferSchema = z.object({
  name: z.string().min(1, 'Name is required.'),
  sourceDealerId: z.string().min(1, 'Your Dealership is required.'),
  destinationDealerId: z.string().min(1, 'Transfer to Dealership is required.'),
  vehicleStockNumber: z.string().min(1, 'Vehicle stock number is required.'),
  make: z.string().min(1, 'Make is required.'),
  model: z.string().min(1, 'Model is required.'),
  color: z.string().min(1, 'Color is required.'),
  effectiveStockTransferDate: z.string()
    .min(1, 'Effective stock transfer date is required.')
    // No longer restrict to "tomorrow onwards".
    // Keep a simple validity check so the field contains a parsable date.
    .refine((val) => {
      // allow any non-empty, valid date string (ISO yyyy-mm-dd from input[type=date])
      return !isNaN(Date.parse(val))
    }, { message: 'Effective transfer date must be a valid date.' }),
  status: z.string().min(1, 'Status is required.'),
  brand: z.string().min(1, 'Brand is required.')
})

const useForm = (initialData, validationSchema) => {
  const [formData, setFormData] = useState(initialData)
  const [errors, setErrors] = useState({})

  const handleInputChange = (e) => {
    const { name, value } = e.target
    setFormData(prev => ({
      ...prev,
      [name]: value
    }))
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }))
    }
  }

  const validateForm = () => {
    if (!validationSchema) return true

    const result = validationSchema.safeParse(formData)
    if (!result.success) {
      const formErrors = z.flattenError(result.error)
      const newErrors = {}
      for (const field in formErrors.fieldErrors) {
        const messages = formErrors.fieldErrors[field]
        if (messages && messages.length > 0) {
          newErrors[field] = messages[0]
        }
      }
      setErrors(newErrors)
      return false
    }
    setErrors({})
    return true
  }

  return {
    formData,
    setFormData,
    errors,
    setErrors,
    handleInputChange,
    validateForm
  }
}

export const StockTransferPage = ({ dataset, umbraco }) => {
  const formatDateYYYYMMDD = (d) => {
    const y = d.getFullYear()
    const m = String(d.getMonth() + 1).padStart(2, '0')
    const day = String(d.getDate()).padStart(2, '0')
    return `${y}-${m}-${day}`
  }

  const today = new Date()
  today.setHours(0, 0, 0, 0)
  const tomorrow = new Date(today)
  tomorrow.setDate(today.getDate() + 1)
  const minDateTomorrow = formatDateYYYYMMDD(tomorrow)
  const umbracoRef = useRef(umbraco || JSON.parse(dataset.umbraco))
  umbraco ||= umbracoRef.current

  const [isSubmitting, setIsSubmitting] = useState(false)
  const [showConfirmation, setShowConfirmation] = useState(false)
  const [isAuthorized, setIsAuthorized] = useState(false)

  // Initialize umbraco data and validate authorization
  useEffect(() => {
    try {
      const umbracoData = umbraco || JSON.parse(dataset.umbraco)
      if (!umbracoData) {
        window.alert('Error: Configuration for this stock transfer is missing')
        window.location.href = '/'
        return
      }
      
      setIsAuthorized(true)
    } catch (error) {
      window.alert('Error: Configuration for this stock transfer is missing')
      window.location.href = '/'
      setIsAuthorized(false)
    }
  }, [dataset.umbraco, umbraco])

  const allDealerships = (umbraco?.Dealers || [])
    .sort((a, b) => a.Name.localeCompare(b.Name))
    .map(dealer => ({
      value: dealer.Id,
      label: dealer.Name
    }))

  // Helper function to find dealer by ID
  const findDealerById = (dealerId) => {
    if (!dealerId || !umbraco?.Dealers) return null
    const id = typeof dealerId === 'string' ? parseInt(dealerId, 10) : dealerId
    return umbraco.Dealers.find(dealer => dealer.Id === id) || null
  }

  const initialFormData = {
    name: '',
    sourceDealerId: '',
    destinationDealerId: '',
    vehicleStockNumber: '',
    make: '',
    model: '',
    color: '',
    effectiveStockTransferDate: '',
    status: 'pending',
    brand: umbraco?.Brand || ''
  }

  const {
    formData,
    errors,
    setErrors,
    handleInputChange,
    validateForm
  } = useForm(initialFormData, stockTransferSchema)

  // Compute selected dealers after formData is available
  const selectedSourceDealer = findDealerById(formData.sourceDealerId)
  const selectedDestinationDealer = findDealerById(formData.destinationDealerId)

  const { trigger: submitStockTransfer } = useSWRMutation(
    '/api/stock/transfer',
    fetcher
  )

  const handleSubmit = async (e) => {
    e.preventDefault()

    if (!validateForm()) {
      console.log('The form is not valid')
      return
    }

    setIsSubmitting(true)

    try {
      const validatedData = stockTransferSchema.parse(formData)
      await submitStockTransfer({
        method: 'POST',
        body: JSON.stringify(validatedData),
        headers: { 'Content-Type': 'application/json' }
      })
      setShowConfirmation(true)
    } catch (error) {
      console.error('Error submitting stock transfer:', error)
      setErrors({ submit: 'Failed to submit request. Please try again.' })
    } finally {
      setIsSubmitting(false)
    }
  }

  if (!isAuthorized) {
    return null
  }

  return (
    <>
      <header data-theme='light' className='_site-header mx-auto flex max-w-7xl items-center justify-between py-4 px-4 md:px-6 lg:px-8'>
        <div className='flex text-white mr-2 gap-x-4 items-center'>
          <a href='/' className='-m-1.5 p-1.5'>
            <span className='sr-only'>UDC</span>
            <BrandLogo className='hidden custom-lg:block' />
            <BrandLogoIcon className='custom-lg:hidden' />
          </a>
          <div className='h-6 border-l border-default-300' />
          { umbraco.Logo && umbraco.Logo.Url && (
              <div className='flex h-[53px] pl-1'>
                <img className='h-full w-auto' src={umbraco.Logo.Url} alt={umbraco.BrandDisplayName} />
              </div>
            )}
        </div>
      </header>
      <section className='relative isolate pt-12 pb-16 px-4 md:px-6 lg:px-10 xl:px-24'>
        <header className='max-w-[680px] text-center mx-auto mb-8'>
          <span className='uppercase block text-[10px] font-extrabold text-default-400 tracking-[2.025px] mb-2.5'>Inter-dealership Vehicle Transfer Form</span>
          <h1 className='text-3xl font-extrabold tracking-tight text-primary-950 sm:text-4xl text-balance mb-2.5'>
            {!showConfirmation ? (
              <>
                {umbraco.BrandDisplayName} Dealer Vehicle Transfer
              </>
            ) : (
              <>
                Your vehicle transfer request has been submitted.
              </>
            )}
          </h1>
          <div className='_simple-rte mt-5 text-base font-semibold tracking-tight text-[--base-text]'>
            {!showConfirmation ? (
              <p>Below you will find the necessary information required in order to transfer a unit from your bailment facility to another dealership within the {umbraco.BrandDisplayName} Dealer Network.</p>
            ) : (
              <>
                <p>Once the vehicle transfer has been confirmed by the receiving dealership, we will send you an email confirmation.</p>
                <p>Should you have any issues with completing the vehicle transfer, please contact your UDC Account Manager.</p>
              </>
            )}
          </div>
        </header>
        {!showConfirmation && (
        <div className='mx-auto max-w-[680px] space-y-6'>
          <div className='mx-auto space-y-8'>
              <div className='space-y-3 border-t border-default-300 pt-6'>
                <div className='font-extrabold space-y-3 text-primary-950'>
                  <h2 className='text-xl'>Instructions to Complete:</h2>
                  <p>
                    Once you have completed this form and click “Submit”, the receiving dealership will receive an email to accept the vehicle transfer.
                  </p>
                  <p>
                    Once the receiving dealership has accepted the vehicle transfer, UDC Finance will process the vehicle transfer between the bailment facilities, and the respective stock unit will transfer. Once this has been completed you will be able to view this in UDCLive.
                  </p>
                </div>
                <form className='space-y-6 pt-6'>
                  <div className='grid grid-cols-1 sm:grid sm:grid-cols-1 gap-6'>
                    <FormField
                      label='Your name'
                      id='name'
                      name='name'
                      value={formData.name}
                      onChange={handleInputChange}
                      placeholder='Enter your full name'
                      error={errors.name}
                      required
                      className='pb-2'
                    />
                  </div>
                  <div className='grid grid-cols-1 gap-6'>
                    <div>
                      <FormField
                        label='Your Dealership'
                        id='sourceDealerId'
                        name='sourceDealerId'
                        type='select'
                        value={formData.sourceDealerId}
                        onChange={handleInputChange}
                        options={allDealerships}
                        placeholder='Select'
                        error={errors.sourceDealerId}
                        className='pb-2'
                        required
                      />
                      {selectedSourceDealer && (
                        <div className='mt-2 space-y-1'>
                          <p className='text-base sm:text-sm text-primary-600'>
                            <span className='font-semibold pr-3'>Dealer Name:</span> {selectedSourceDealer.Name}
                          </p>
                          {selectedSourceDealer.PhoneNumber && (
                            <p className='text-base sm:text-sm text-primary-600'>
                              <span className='font-semibold pr-3'>Dealer Phone:</span> {selectedSourceDealer.PhoneNumber}
                            </p>
                          )}
                          {(selectedSourceDealer.StockTransferEmail?.trim() || selectedSourceDealer.DealerEmail) && (
                            <p className='text-base sm:text-sm text-primary-600'>
                              <span className='font-semibold pr-3'>Dealer Email:</span> {selectedSourceDealer.StockTransferEmail?.trim() || selectedSourceDealer.DealerEmail}
                            </p>
                          )}
                        </div>
                      )}
                    </div>
                    <div>
                      <FormField
                        label='Transfer to Dealership'
                        id='destinationDealerId'
                        name='destinationDealerId'
                        type='select'
                        value={formData.destinationDealerId}
                        onChange={handleInputChange}
                        options={allDealerships}
                        placeholder='Select'
                        error={errors.destinationDealerId}
                        className='pb-2'
                        required
                      />
                      {selectedDestinationDealer && (
                        <div className='mt-2 space-y-1'>
                          <p className='text-base sm:text-sm text-primary-600'>
                            <span className='font-semibold pr-3'>Dealer Name:</span> {selectedDestinationDealer.Name}
                          </p>
                          {selectedDestinationDealer.PhoneNumber && (
                            <p className='text-base sm:text-sm text-primary-600'>
                              <span className='font-semibold pr-3'>Dealer Phone:</span> {selectedDestinationDealer.PhoneNumber}
                            </p>
                          )}
                          {(selectedDestinationDealer.StockTransferEmail?.trim() || selectedDestinationDealer.DealerEmail) && (
                            <p className='text-base sm:text-sm text-primary-600'>
                              <span className='font-semibold pr-3'>Dealer Email:</span> {selectedDestinationDealer.StockTransferEmail?.trim() || selectedDestinationDealer.DealerEmail}
                            </p>
                          )}
                        </div>
                      )}
                    </div>
                  </div>

                  <div className='grid grid-cols-1 gap-6'>
                    <FormField
                      label='Vehicle Stock Number'
                      id='vehicleStockNumber'
                      name='vehicleStockNumber'
                      value={formData.vehicleStockNumber}
                      onChange={handleInputChange}
                      placeholder='Enter vehicle stock number'
                      error={errors.vehicleStockNumber}
                      required
                      className='pb-2'
                    />

                    <FormField
                      label='Vehicle Model'
                      id='model'
                      name='model'
                      value={formData.model}
                      onChange={handleInputChange}
                      placeholder='Enter vehicle model'
                      error={errors.model}
                      required
                      className='pb-2'
                    />
                  </div>

                  <div className='grid grid-cols-1 gap-6'>
                    <FormField
                      label='Vehicle Make'
                      id='make'
                      name='make'
                      value={formData.make}
                      onChange={handleInputChange}
                      placeholder='Enter vehicle make'
                      error={errors.make}
                      required
                      className='pb-2'
                    />
                    <FormField
                      label='Vehicle Colour'
                      id='color'
                      name='color'
                      value={formData.color}
                      onChange={handleInputChange}
                      placeholder='Enter vehicle colour'
                      error={errors.color}
                      required
                      className='pb-2'
                    />
                  </div>

                  <div className='grid grid-cols-1 sm:grid sm:grid-cols-1 gap-6'>
                    <FormField
                      label='Effective Transfer Date'
                      id='effectiveStockTransferDate'
                      name='effectiveStockTransferDate'
                      type='date'
                      value={formData.effectiveStockTransferDate}
                      onChange={handleInputChange}
                      placeholder='(DD/MM/YYYY)'
                      error={errors.effectiveStockTransferDate}
                      required
                      className='pb-2'
                    />
                  </div>

                  <FormError error={errors.submit} />

                  <div className='flex justify-end'>
                    <Button
                      onClick={handleSubmit}
                      disabled={isSubmitting}
                      variant='primary'
                      size='lg'
                      className='pl-8 pr-8'
                    >
                      <span className='block relative pr-7'>
                        {isSubmitting ? 'Submitting...' : 'Submit'} <ArrowRight className='absolute -right-0.5 top-1/2 -mt-[11px]' />
                      </span>
                    </Button>
                  </div>
                </form>

                <div className='text-sm font-semibold space-y-3 pt-6 text-[--base-text]'>
                  <p>Should you have any questions or issues completing the vehicle transfer, please contact your UDC Account Manager.</p>
                  <p>By accepting this transfer of the above vehicle, you acknowledge you have obligations in respect of this transfer pursuant to your bailment agreement.</p>
                  <p className='text-default-500 font-extrabold pt-16'>*These fields are mandatory</p>
                </div>
              </div>
            </div>
          </div>
        )}
      </section>
    </>
  )
}

reactMount('StockTransferPage', StockTransferPage)
